import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ChatBot from './components/ChatBot';
import NewsletterPopup from './components/NewsletterPopup';
import LoginPage from './pages/LoginPage';
import AdminSetupPage from './pages/AdminSetupPage';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import SizeGuidePage from './pages/SizeGuidePage';
import AdminPage from './pages/AdminPage';
import ProductEditPage from './pages/ProductEditPage';
import AccountPage from './pages/AccountPage';
import InvoicingPage from './pages/InvoicingPage';
import MigrationPage from './pages/MigrationPage';
import GiftCardsPage from './pages/GiftCardsPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import CategoriesPage from './pages/CategoriesPage';
import LegalPage from './pages/LegalPage';
import DocumentsPage from './pages/DocumentsPage';
import DownloadsPage from './pages/DownloadsPage';
import useStore from './store/useStore';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Composant de protection des routes - nécessite une connexion
const PrivateRoute = ({ children }) => {
  const { user } = useStore();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Composant de protection des routes admin
const AdminRoute = ({ children }) => {
  const { user } = useStore();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role !== 'admin') {
    return <Navigate to="/shop" replace />;
  }
  
  return children;
};

// Layout principal avec Navbar et Footer (pour les utilisateurs connectés)
const MainLayout = ({ children, showChatBot }) => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">
        {children}
      </main>
      <Footer />
      {showChatBot && <ChatBot />}
      <NewsletterPopup />
    </div>
  );
};

// Composant qui détermine le layout basé sur la route
const AppContent = () => {
  const { user, setSiteSettings, siteSettings, initAuth } = useStore();
  const location = useLocation();

  // Restore user auth from localStorage on mount (prevents redirect to login after page reload)
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Load site settings on app start
  useEffect(() => {
    const loadSiteSettings = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings/site`);
        if (response.ok) {
          const data = await response.json();
          setSiteSettings(data);
        }
      } catch (error) {
        console.error('Error loading site settings:', error);
      }
    };
    loadSiteSettings();

    // Listen for settings updates from admin panel (avoids page reload)
    const handleSettingsUpdate = (e) => {
      if (e.detail) {
        setSiteSettings(e.detail);
      }
    };
    window.addEventListener('site-settings-updated', handleSettingsUpdate);
    return () => window.removeEventListener('site-settings-updated', handleSettingsUpdate);
  }, [setSiteSettings]);

  // Page de login sans layout
  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
      </Routes>
    );
  }

  // Page de setup admin sans layout (première configuration)
  if (location.pathname === '/setup') {
    return (
      <Routes>
        <Route path="/setup" element={<AdminSetupPage />} />
      </Routes>
    );
  }

  // Rediriger vers login si non connecté
  if (!user && location.pathname !== '/login') {
    return <Navigate to="/login" replace />;
  }

  const showChatBot = siteSettings?.chatbot_enabled !== false;

  return (
    <MainLayout showChatBot={showChatBot}>
      <Routes>
        {/* Route par défaut - redirige vers shop pour les clients, admin pour les admins */}
        <Route 
          path="/" 
          element={
            <PrivateRoute>
              {user?.role === 'admin' ? <Navigate to="/admin" replace /> : <Navigate to="/shop" replace />}
            </PrivateRoute>
          } 
        />
        
        {/* Routes accessibles à tous les utilisateurs connectés */}
        <Route path="/shop" element={<PrivateRoute><ShopPage /></PrivateRoute>} />
        <Route path="/shop/:category" element={<PrivateRoute><ShopPage /></PrivateRoute>} />
        <Route path="/product/:productId" element={<PrivateRoute><ProductDetailPage /></PrivateRoute>} />
        <Route path="/cart" element={<PrivateRoute><CartPage /></PrivateRoute>} />
        <Route path="/checkout" element={<PrivateRoute><CheckoutPage /></PrivateRoute>} />
        <Route path="/contact" element={<PrivateRoute><ContactPage /></PrivateRoute>} />
        <Route path="/size-guide" element={<PrivateRoute><SizeGuidePage /></PrivateRoute>} />
        <Route path="/account" element={<PrivateRoute><AccountPage /></PrivateRoute>} />
        <Route path="/downloads" element={<PrivateRoute><DownloadsPage /></PrivateRoute>} />
        
        {/* Pages légales - accessibles à tous les utilisateurs connectés */}
        <Route path="/legal/:section" element={<PrivateRoute><LegalPage /></PrivateRoute>} />
        
        {/* Routes réservées à l'admin */}
        <Route path="/home" element={<AdminRoute><HomePage /></AdminRoute>} />
        <Route path="/about" element={<AdminRoute><AboutPage /></AdminRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/admin/product/:productId" element={<AdminRoute><ProductEditPage /></AdminRoute>} />
        <Route path="/admin/invoicing" element={<AdminRoute><InvoicingPage /></AdminRoute>} />
        <Route path="/admin/migration" element={<AdminRoute><MigrationPage /></AdminRoute>} />
        <Route path="/admin/gift-cards" element={<AdminRoute><GiftCardsPage /></AdminRoute>} />
        <Route path="/admin/email-templates" element={<AdminRoute><EmailTemplatesPage /></AdminRoute>} />
        <Route path="/admin/categories" element={<AdminRoute><CategoriesPage /></AdminRoute>} />
        <Route path="/admin/documents" element={<AdminRoute><DocumentsPage /></AdminRoute>} />
        
        {/* Fallback - redirige vers shop */}
        <Route path="*" element={<Navigate to="/shop" replace />} />
      </Routes>
    </MainLayout>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
